import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { X, ArrowRight, ArrowLeft, Check, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { CategoryData, ServiceItem, TimeSlot } from "@/types";
import { createAppointmentToken } from "@/utils/tokenUtils";
import { findNextAvailableDate, generateTimeSlots } from "@/utils/dateUtils";
import ServiceSelection from "./ServiceSelection";
import DateTimeSelection from "./DateTimeSelection";
import ConfirmationForm, { bookingFormSchema } from "./ConfirmationForm";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useIsMobile } from "@/hooks/use-mobile";
import { useGoogleAnalytics } from "@/hooks/useGoogleAnalytics";
import { useConsentRequired } from "@/hooks/useConsentRequired";
import { useSupabaseHealth } from "@/hooks/useSupabaseHealth";
import { useCalendarSettings } from "@/hooks/useCalendarSettings";
import { useWorkingHours } from "@/hooks/useWorkingHours";
import { useBreaks } from "@/hooks/useBreaks";
import { useRecurringBreaks } from "@/hooks/useRecurringBreaks";
import { useServicesData } from "@/hooks/useServicesData";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const STEPS = {
  SELECT_SERVICES: 0,
  SELECT_DATETIME: 1,
  CONFIRMATION: 2,
};

const BookingModal: React.FC<BookingModalProps> = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(STEPS.SELECT_SERVICES);
  const [selectedServices, setSelectedServices] = useState<ServiceItem[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(null);
  const [clientInfo, setClientInfo] = useState({
    name: "",
    email: "",
    phone: "",
    notes: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isMobile = useIsMobile();
  const { trackEvent, trackConversion } = useGoogleAnalytics();
  const { settings: calendarSettings } = useCalendarSettings();
  const bookingBufferHours = calendarSettings?.booking_buffer_hours ?? 2;
  const { isRestricted } = useSupabaseHealth();

  // Use shared cached hooks instead of internal fetchData
  const { workingHours = [] } = useWorkingHours();
  const { breaks = [] } = useBreaks();
  const { recurringBreaks = [] } = useRecurringBreaks();
  const { categories: categoriesMap = {}, services: allServices = [], isLoading: servicesLoading } = useServicesData();

  // Fetch appointment slots only when user reaches date selection step (lazy load)
  const { data: appointments = [], isLoading: appointmentsLoading } = useQuery({
    queryKey: ["booking-appointment-slots"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_public_time_slots");
      if (error) throw error;
      return (data || []).map((s: any) => ({
        date: s.date,
        start_time: s.start_time,
        end_time: s.end_time,
      }));
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: step >= STEPS.SELECT_DATETIME,
  });

  const isLoading = servicesLoading || appointmentsLoading;

  // Convert categories map to array for ServiceSelection
  const categories = useMemo(() => {
    return Object.values(categoriesMap).map(cat => ({
      ...cat,
      services: cat.services || [],
    }));
  }, [categoriesMap]);

  // Convert breaks for date utils
  const breaksForSlots = useMemo(() => {
    return breaks.map(b => ({ date: b.date, start_time: b.start_time, end_time: b.end_time }));
  }, [breaks]);

  useConsentRequired();

  useEffect(() => {
    if (isOpen) {
      trackEvent("appointment_started");
    } else {
      resetForm();
    }
  }, [isOpen, trackEvent]);

  // Recalculer la date quand les services changent
  useEffect(() => {
    if (selectedServices.length > 0 && workingHours.length > 0) {
      const totalDuration = selectedServices.reduce((total, service) => {
        const match = service.duration.match(/(\d+)/);
        const minutes = match ? parseInt(match[1]) : 0;
        const bufferMinutes = service.buffer_time || 0;
        const bufferBeforeMinutes = service.buffer_time_before || 0;
        return total + minutes + bufferMinutes + bufferBeforeMinutes;
      }, 0);

      const totalBufferBefore = selectedServices.reduce((total, service) => {
        return total + (service.buffer_time_before || 0);
      }, 0);

      const slots = generateTimeSlots(selectedDate, workingHours, appointments, totalDuration, bookingBufferHours, totalBufferBefore, breaksForSlots, recurringBreaks);

      if (slots.length === 0) {
        const nextDate = findNextAvailableDate(workingHours, appointments, totalDuration, 60, bookingBufferHours, totalBufferBefore, breaksForSlots, recurringBreaks);
        setSelectedDate(nextDate);
        setSelectedTimeSlot(null);
      }
    }
    // selectedDate volontairement absent : on n'évalue qu'à chaque changement de
    // services / horaires / agenda — sans risquer une boucle de re-positionnement.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedServices, workingHours, appointments, breaksForSlots, recurringBreaks, bookingBufferHours]);

  // Set initial date when data loads
  useEffect(() => {
    if (!isLoading && workingHours.length > 0 && isOpen) {
      const initialDate = findNextAvailableDate(
        workingHours,
        appointments,
        60,
        60,
        bookingBufferHours,
        0,
        breaksForSlots,
        recurringBreaks,
      );
      setSelectedDate(initialDate);
    }
  }, [isLoading, workingHours.length, isOpen]);

  const handleNextStep = () => {
    if (step === STEPS.SELECT_SERVICES) {
      if (selectedServices.length === 0) {
        toast({
          title: "Sélection requise",
          description: "Veuillez sélectionner au moins un service",
          variant: "destructive",
        });
        return;
      }
      setStep(STEPS.SELECT_DATETIME);
    } else if (step === STEPS.SELECT_DATETIME) {
      if (!selectedTimeSlot) {
        toast({
          title: "Sélection requise",
          description: "Veuillez sélectionner un créneau horaire",
          variant: "destructive",
        });
        return;
      }
      setStep(STEPS.CONFIRMATION);
    }
  };

  const handlePrevStep = () => {
    if (step > STEPS.SELECT_SERVICES) {
      setStep(step - 1);
    }
  };

  const isFormValid = () => {
    try {
      bookingFormSchema.parse(clientInfo);
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleSubmitBooking = async () => {
    if (isRestricted) {
      toast({
        title: "Réservation en ligne indisponible",
        description: "Merci de me contacter directement par téléphone ou email pour prendre rendez-vous.",
        variant: "destructive",
      });
      return;
    }
    if (!isFormValid()) {
      toast({
        title: "Champs incomplets",
        description: "Veuillez remplir tous les champs obligatoires",
        variant: "destructive",
      });
      return;
    }
    try {
      setIsSubmitting(true);

      const serviceDuration = selectedServices.reduce((total, service) => {
        const match = service.duration.toString().match(/(\d+)/);
        const minutes = match ? parseInt(match[1]) : 0;
        return total + minutes;
      }, 0);

      const totalBufferAfter = selectedServices.reduce((sum, s) => sum + (s.buffer_time || 0), 0);
      const totalBufferBefore = selectedServices.reduce((sum, s) => sum + (s.buffer_time_before || 0), 0);

      const appointmentId = crypto.randomUUID();

      const displayedStartTimeParts = selectedTimeSlot!.startTime.split(":");
      const actualStartDateTime = new Date(selectedDate);
      actualStartDateTime.setHours(parseInt(displayedStartTimeParts[0]));
      actualStartDateTime.setMinutes(parseInt(displayedStartTimeParts[1]) - totalBufferBefore);
      const actualStartTime = format(actualStartDateTime, "HH:mm:ss");

      const endDateTime = new Date(selectedDate);
      endDateTime.setHours(parseInt(displayedStartTimeParts[0]));
      endDateTime.setMinutes(parseInt(displayedStartTimeParts[1]) + serviceDuration + totalBufferAfter);
      const finalEndTime = format(endDateTime, "HH:mm:ss");

      // Normalisation : email en minuscules + téléphone en chiffres uniquement
      // → garantit la reconnaissance de la cliente par le trigger DB et évite les doublons
      const normalizedEmail = clientInfo.email.trim().toLowerCase();
      const normalizedPhone = clientInfo.phone.replace(/\D/g, "");

      const { error: appointmentError } = await supabase.from("appointments").insert({
        id: appointmentId,
        client_name: clientInfo.name.trim(),
        client_email: normalizedEmail,
        client_phone: normalizedPhone,
        date: format(selectedDate, "yyyy-MM-dd"),
        start_time: actualStartTime,
        end_time: finalEndTime,
        notes: clientInfo.notes || null,
        status: "pending",
      });
      if (appointmentError) throw appointmentError;

      const appointmentServices = selectedServices.map((service) => ({
        appointment_id: appointmentId,
        service_id: service.id,
      }));
      const { error: servicesError } = await supabase.from("appointment_services").insert(appointmentServices);
      if (servicesError) throw servicesError;

      const cancelToken = await createAppointmentToken(appointmentId, "client_cancel", 30);

      const appointmentData = {
        id: appointmentId,
        client_name: clientInfo.name.trim(),
        client_email: normalizedEmail,
        client_phone: normalizedPhone,
        date: format(selectedDate, "yyyy-MM-dd"),
        start_time: selectedTimeSlot!.startTime + ":00",
        end_time: selectedTimeSlot!.endTime + ":00",
        notes: clientInfo.notes || null,
      };

      await sendBookingEmails(appointmentData, selectedServices, cancelToken);
      toast({
        title: "Rendez-vous enregistré",
        description:
          "Votre demande de rendez-vous a été envoyée avec succès. Vous recevrez une confirmation par email. Pensez à vérifier vos spams.",
      });

      const totalValue = selectedServices.reduce((sum, service) => {
        const priceMatch = service.price.match(/(\d+)/);
        return sum + (priceMatch ? parseInt(priceMatch[1]) : 0);
      }, 0);
      trackConversion("appointment_completed", totalValue);

      resetForm();
      onClose();
    } catch (error: any) {
      console.error("Error booking appointment:", error);
      toast({
        title: "Erreur",
        description: error.message || "Une erreur est survenue lors de la prise de rendez-vous",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const sendBookingEmails = async (appointmentData: any, services: ServiceItem[], cancelToken: string | null) => {
    try {
      const confirmToken = await createAppointmentToken(appointmentData.id, "confirm");
      const cancelAdminToken = await createAppointmentToken(appointmentData.id, "cancel");

      await supabase.functions.invoke("send-email", {
        body: {
          type: "booking_admin",
          data: {
            appointment_id: appointmentData.id,
            client_name: appointmentData.client_name,
            client_email: appointmentData.client_email,
            client_phone: appointmentData.client_phone || "",
            date: appointmentData.date,
            start_time: appointmentData.start_time.substring(0, 5),
            end_time: appointmentData.end_time.substring(0, 5),
            services: services.map((service) => ({
              name: service.name,
              price: service.price,
            })),
            notes: appointmentData.notes || "",
            confirm_token: confirmToken,
            cancel_token: cancelAdminToken,
          },
        },
      });

      await supabase.functions.invoke("send-email", {
        body: {
          type: "booking_user_confirmation",
          data: {
            client_name: appointmentData.client_name,
            client_email: appointmentData.client_email,
            date: appointmentData.date,
            start_time: appointmentData.start_time.substring(0, 5),
            end_time: appointmentData.end_time.substring(0, 5),
            services: services.map((service) => ({
              name: service.name,
              price: service.price,
            })),
            token: cancelToken,
          },
        },
      });
    } catch (error) {
      console.error("Erreur lors de l'envoi des emails:", error);
    }
  };

  const resetForm = () => {
    setStep(STEPS.SELECT_SERVICES);
    setSelectedServices([]);
    setSelectedDate(new Date());
    setSelectedTimeSlot(null);
    setClientInfo({
      name: "",
      email: "",
      phone: "",
      notes: "",
    });
  };

  const getStepTitle = () => {
    switch (step) {
      case STEPS.SELECT_SERVICES:
        return "Sélection des services";
      case STEPS.SELECT_DATETIME:
        return "Choix de la date et heure";
      case STEPS.CONFIRMATION:
        return "Confirmation";
      default:
        return "";
    }
  };

  const getStepSubtitle = () => {
    switch (step) {
      case STEPS.SELECT_SERVICES:
        return "Sélectionnez un ou plusieurs services pour votre rendez-vous.";
      case STEPS.SELECT_DATETIME:
        return "Sélectionnez une date et un horaire pour votre rendez-vous.";
      case STEPS.CONFIRMATION:
        return "Vérifiez vos informations avant de confirmer votre rendez-vous.";
      default:
        return "";
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        hideCloseButton
        className="sm:max-w-lg w-[calc(100%-2rem)] overflow-hidden flex flex-col rounded-xl max-h-[calc(100vh-40px)] pt-3 pb-0 gap-0"
      >
        <DialogHeader className="border-b pb-3 pr-8 flex-shrink-0 space-y-0">
          <DialogTitle className="text-xl">{getStepTitle()}</DialogTitle>

          <p className="mt-0 text-sm text-muted-foreground leading-snug">{getStepSubtitle()}</p>

          <Button variant="ghost" size="icon" onClick={onClose} className="absolute right-4 top-4">
            <X className="h-4 w-4" />
          </Button>
        </DialogHeader>

        <ScrollArea className="flex-grow overflow-y-auto px-1 pb-4 md:pb-6">
          <div className="px-0 pt-[7px] pb-0">
            {step === STEPS.SELECT_SERVICES && (
              <ServiceSelection
                categories={categories}
                selectedServices={selectedServices}
                onSelectService={(service) => {
                  if (selectedServices.some((s) => s.id === service.id)) {
                    setSelectedServices(selectedServices.filter((s) => s.id !== service.id));
                  } else {
                    setSelectedServices([...selectedServices, service]);
                  }
                }}
                isLoading={isLoading}
              />
            )}

            {step === STEPS.SELECT_DATETIME && (
              <DateTimeSelection
                selectedDate={selectedDate}
                onDateChange={setSelectedDate}
                selectedTimeSlot={selectedTimeSlot}
                onTimeSlotSelect={setSelectedTimeSlot}
                workingHours={workingHours}
                appointments={appointments}
                selectedServices={selectedServices}
                breaks={breaksForSlots}
                recurringBreaks={recurringBreaks}
              />
            )}

            {step === STEPS.CONFIRMATION && (
              <ConfirmationForm
                selectedServices={selectedServices}
                selectedDate={selectedDate}
                selectedTimeSlot={selectedTimeSlot}
                clientInfo={clientInfo}
                onClientInfoChange={setClientInfo}
              />
            )}
          </div>
        </ScrollArea>

        <div className="sticky bottom-0 bg-white/90 backdrop-blur-sm border-t w-full flex flex-col">
          {/* Bande date collée au-dessus des boutons */}
          {step === STEPS.SELECT_DATETIME && selectedTimeSlot && (
            <div className="w-full bg-bbz-green/10 border-b border-bbz-green/20 px-4 py-2 text-center">
              <span className="text-sm font-medium text-bbz-text capitalize leading-tight">
                {format(selectedDate, "EEEE dd MMMM", { locale: fr })} à {selectedTimeSlot.startTime}
              </span>
            </div>
          )}

          {/* Alerte restriction Supabase */}
          {isRestricted && (
            <div
              role="alert"
              className="w-full bg-destructive/10 border-b border-destructive/30 px-4 py-2.5 flex items-start gap-2"
            >
              <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" aria-hidden="true" />
              <p className="text-xs text-bbz-text leading-snug">
                Réservation en ligne temporairement indisponible. Merci de me contacter par téléphone (
                <a href="tel:+33499538308" className="underline font-medium">04 99 53 83 08</a>) ou par{" "}
                <a href="mailto:contact@biobeautezen.com" className="underline font-medium">email</a>.
              </p>
            </div>
          )}

          {/* Ligne des boutons */}
          <div className="flex justify-between w-full px-6 py-4">
            {step > STEPS.SELECT_SERVICES ? (
              <Button variant="outline" onClick={handlePrevStep} className="flex items-center">
                <ArrowLeft className="h-4 w-4 mr-2" /> Retour
              </Button>
            ) : (
              <div></div>
            )}

            {step < STEPS.CONFIRMATION ? (
              <Button onClick={handleNextStep} className="flex items-center">
                Continuer <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmitBooking}
                className={`flex items-center text-white transition-all ${
                  isFormValid() && !isSubmitting && !isRestricted
                    ? "bg-bbz-green-dark hover:bg-bbz-green-dark/90 shadow-md hover:shadow-lg"
                    : "bg-bbz-green/30 cursor-not-allowed"
                }`}
                disabled={isSubmitting || !isFormValid() || isRestricted}
              >
                {isSubmitting ? (
                  <>Traitement en cours...</>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" /> Confirmer la réservation
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
export default BookingModal;
